<template>
    <!-- Via https://codepen.io/amiechen/pen/xjvMGb -->
    <!-- mobile-tap-target opts this field into a 48px (~8mm) minimum height and a
         16px font on phones, matching an accessible touch target and stopping iOS
         Safari's focus auto-zoom (it zooms any input under 16px). Off by default so
         every existing consumer keeps its current size; see the scoped CSS. -->
    <div :class="{'border-red-500 bg-red-50': hasError, 'border-gray-300': !hasError, 'mobile-tap-target': mobileTapTarget}"
         class="relative border rounded mb-4 appearance-none label-floating">
        <input class="w-full py-2 px-3 text-gray-800 leading-normal rounded text-base border-0 focus:ring-0 focus:outline-none"
               style="border-width: 0;"
               ref="input"
               :class="{'text-center' : centered, 'cursor-not-allowed': readonly, 'border-red-500': hasError}"
               v-model="value"
               @change="$emit('update:value', $event.target.value)"
               @input="$emit('update:value', $event.target.value)"
               :id="$data._id"
               :name="$data._id"
               type="text"
               placeholder=" "
               :readonly="readonly">
        <label :class="{'text-center' : centered, 'text-red-600': hasError, 'text-gray-800': !hasError}"
               :for="$data._id"
               class="absolute block top-0 left-0 w-full pb-2 pr-3 leading-normal cursor-text overflow-hidden text-nowrap"
               style="padding-top: 0.58rem; padding-left: 0.82rem;"
        >
          {{ label }}<span v-if="required" class="text-red-500 ml-1">*</span>
        </label>
        <div    v-if="!readonly && value.length > 0"
                @click="clearValue"
                class="absolute inset-y-0 right-0 pr-3 flex items-center">
            <span class="lnr icon-cross cursor-pointer text-gray-400 hover:text-gray-700 text-xs"></span>

        </div>
        <div v-if="hasError && errorMessage" class="text-red-600 text-xs mt-1">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>

    // Next ID for the floating Input
    let nextId = 0

    export default {
        name: "FloatingInput",
        emits: ['update:value', 'update:id'],
        props: {
            value: {
                type: [String, Number],
                default: '',
            },
            label: {
                type: String,
                default: 'Label',
            },
            id: {
                type: String,
                default() {
                    return `floating_input`
                },
            },
            centered: {
                type: Boolean,
                default: false,
            },
            readonly: {
                type: Boolean,
                default: false,
            },
            required: {
                type: Boolean,
                default: false,
            },
            hasError: {
                type: [Boolean, Array],
                default: false,
            },
            errorMessage: {
                type: [String, Array],
                default: '',
            },
            // Opt in to an accessible mobile treatment: a 48px (~8mm) minimum
            // height and a 16px font on phones. Off by default so existing
            // consumers keep their current size. See the scoped CSS.
            mobileTapTarget: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                _id: this.id,
            }
        },
        created() {
            this.updateId()
        },
        methods: {
            clearValue() {
                this.$emit('update:value', '')
            },
            updateId() {
                let newId = this.id
                if (this.id === 'floating_input') {
                    newId += `_${nextId++}`
                }

                this.$data._id = newId
            },
        },
        watch: {
            id() {
                this.$data._id = this.id
                this.updateId()
            },
            _id(val, old) {
                if (val === old) {
                    return
                }

                this.$emit('update:id', val)
            },
        },
    }
</script>

<style scoped>
    .label-floating input:not(:placeholder-shown) {
        padding-top: 1.4rem;
    }
    .label-floating input:not(:placeholder-shown) ~ label {
        font-size: 0.7rem;
        transform: translateY(0);
        transition: all 0.2s ease-in-out;
        font-weight: 700;
    }
    .label-floating input:not(:placeholder-shown) ~ label:not(.text-red-600) {
        color: #1A56DB;
    }
    .label-floating input:placeholder-shown ~ label {
        transform: translateY(-0.15rem);
        transition: all 0.15s ease-in-out;
        color: rgb(107, 114, 128) !important;
        font-weight: normal;
    }

    /*
     * mobile-tap-target: on phones (< sm) grow the field to a 48px (~8mm) tap
     * target and force a 16px font so iOS Safari does not auto-zoom on focus.
     *
     * The label floats over the input and its position is driven by the input's
     * padding, so the height MUST be added as balanced padding, not min-height:
     * a bare min-height grows only the bottom and leaves the placeholder text
     * riding high and the floated label misplaced. With a 16px font the line box
     * is ~24px, so 12px top + 12px bottom centres the placeholder text in the
     * 48px box; the label's padding-top is matched to 12px so it sits exactly on
     * that text while empty. When filled, the label floats up small and the
     * input's padding-top grows (with a matching smaller bottom) so the value
     * clears the floated label while the box height stays 48px.
     */
    @media (max-width: 639px) {
        .mobile-tap-target input {
            font-size: 16px;
            line-height: 24px;
            padding-top: 12px;
            padding-bottom: 12px;
        }
        /* In the empty state the label IS the placeholder, so it must match the
           input's font-size and line-height exactly or it reads higher and
           smaller than the text it stands in for. padding-top overrides the
           label's inline 0.58rem (inline beats scoped, hence !important). The
           floated (filled) label shrinks again via the rule above. */
        .mobile-tap-target input:placeholder-shown ~ label {
            font-size: 16px;
            line-height: 24px;
            padding-top: 12px !important;
            /* The natural field nudges the placeholder label up 0.15rem; with the
               balanced 12px padding here the label is already on the text, so
               drop the nudge to keep it centred. */
            transform: translateY(0);
        }
        .mobile-tap-target input:not(:placeholder-shown) {
            padding-top: 17px;
            padding-bottom: 7px;
        }
    }
</style>